import { c as _c } from "react/compiler-runtime";
import { Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { MPAOperationType, type MPARequest, MPARequestStatus } from '@repo/api-gw-sdk';
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { DIFF_EDITOR_OPTIONS, MonacoDiffEditor, useMonacoTheme } from '@/app/(dashboard)/db/[id]/Editor/monacoEditor';
import { BackupPolicyCard } from '@/app/(dashboard)/settings/backup-policies/BackupPolicyCard';
import { CopyToClipboardButton } from '@/components/buttons/copyToClipboardButton';
import { MainDialog } from '@/components/dialogs/mainDialog';
import { getBackupPolicyId } from '@/components/dialogs/mpa/mpaRequestEntity';
import { JsonViewerPanel } from '@/components/shared/jsonViewer';
import { useDAL } from '@/contexts/useDAL';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { formatJson } from '@/utils/objectUtils';
import { parseJson } from '@/utils/string';
const parseBody = (request: MPARequest): object | null => request.requestBody ? parseJson<object>(request.requestBody) ?? null : null;
const RequestPathHeader = t0 => {
  const $ = _c(10);
  const {
    request
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignItems: "baseline",
      gap: "8px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      fontFamily: "monospace"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== request.requestMethod) {
    t3 = <Typography variant="h4" sx={t2}>{request.requestMethod}</Typography>;
    $[2] = request.requestMethod;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      fontFamily: "monospace",
      wordBreak: "break-all"
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== request.requestPath) {
    t5 = <Typography variant="body1" sx={t4}>{request.requestPath}</Typography>;
    $[5] = request.requestPath;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <Stack direction="row" sx={t1}>{t3}{t5}</Stack>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};

// Default renderer: pretty-printed request body, or an empty-state message.
// Used as the fallback whenever a typed renderer can't display its entity
// (e.g. a delete-policy request whose target policy has already been purged).
const JsonRequestBody = t0 => {
  const $ = _c(5);
  const {
    request
  } = t0;
  let t1;
  if ($[0] !== request) {
    t1 = parseBody(request);
    $[0] = request;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const body = t1;
  if (body) {
    let t2;
    if ($[2] !== body) {
      t2 = <JsonViewerPanel data={body} />;
      $[2] = body;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Typography variant="body1" sx={{
      color: "text.secondary"
    }}>Request body is empty</Typography>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const DeleteBackupPolicyRequestBody = ({
  request
}: {
  request: MPARequest;
  view: string;
}) => {
  const dal = useDAL();
  const policyId = getBackupPolicyId(request);
  const hasBefore = !!request.resourceStateBefore;
  const {
    data: policy,
    isLoading
  } = dal.backupPolicy.useGet(hasBefore ? null : policyId);
  const {
    vaults
  } = useBackupVaults();
  if (isLoading) {
    return null;
  }
  if (policy) {
    return <div className='flex-1 min-h-0 overflow-auto'>
        <BackupPolicyCard policy={policy} vaults={vaults} defaultExpanded />
      </div>;
  }
  if (request.resourceStateBefore) {
    const parsed = parseJson<object>(request.resourceStateBefore);
    if (parsed) {
      return <JsonViewerPanel data={parsed} />;
    }
  }
  return <Typography variant='body1' sx={{
    color: 'text.secondary'
  }}>
      Policy data is not available
    </Typography>;
};
const UpdateBackupPolicyRequestBody = ({
  request
}: {
  request: MPARequest;
  view: string;
}) => {
  const dal = useDAL();
  const policyId = getBackupPolicyId(request);
  const hasBefore = !!request.resourceStateBefore;
  const {
    data: livePolicyResponse,
    isLoading
  } = dal.backupPolicy.useGetV1(hasBefore ? null : policyId);
  const afterState = request.resourceStateAfter ?? request.requestBody;
  const modified = useMemo(() => {
    const parsed = afterState ? parseJson<object>(afterState) ?? null : null;
    return parsed ? formatJson(parsed) : '';
  }, [afterState]);
  const original = useMemo(() => {
    if (request.resourceStateBefore) {
      const parsed_0 = parseJson<object>(request.resourceStateBefore);
      return parsed_0 ? formatJson(parsed_0) : '';
    }
    if (livePolicyResponse?.backupPolicy) {
      return formatJson(livePolicyResponse.backupPolicy);
    }
    return '';
  }, [request.resourceStateBefore, livePolicyResponse]);
  if (isLoading) {
    return null;
  }
  if (!original || !modified) {
    return <RawRequestView request={request} />;
  }
  return <PolicyStateDiff original={original} modified={modified} />;
};

// The gateway returns both sides in the public NotificationPolicy shape, with held credentials as placeholders.
const UpdateNotificationPolicyRequestBody = t0 => {
  const $ = _c(9);
  const {
    request
  } = t0;
  let t1;
  if ($[0] !== request.resourceStateBefore) {
    t1 = formatJsonState(request.resourceStateBefore);
    $[0] = request.resourceStateBefore;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const original = t1;
  let t2;
  if ($[2] !== request.resourceStateAfter) {
    t2 = formatJsonState(request.resourceStateAfter);
    $[2] = request.resourceStateAfter;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const modified = t2;
  if (!original || !modified) {
    let t3;
    if ($[4] !== request) {
      t3 = <RawRequestView request={request} />;
      $[4] = request;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t3;
  if ($[6] !== modified || $[7] !== original) {
    t3 = <PolicyStateDiff original={original} modified={modified} />;
    $[6] = modified;
    $[7] = original;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const formatJsonState = (state: string | null | undefined) => {
  const parsed = state ? parseJson<object>(state) : null;
  return parsed ? formatJson(parsed) : '';
};
const PolicyStateDiff = t0 => {
  const $ = _c(4);
  const {
    original,
    modified
  } = t0;
  const monacoTheme = useMonacoTheme();
  let t1;
  if ($[0] !== modified || $[1] !== monacoTheme || $[2] !== original) {
    t1 = <Stack className="flex-1 min-h-0"><MonacoDiffEditor height="100%" language="json" theme={monacoTheme} original={original} modified={modified} keepCurrentOriginalModel={true} keepCurrentModifiedModel={true} options={DIFF_EDITOR_OPTIONS} /></Stack>;
    $[0] = modified;
    $[1] = monacoTheme;
    $[2] = original;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
interface RequestBodyProps {
  request: MPARequest;
  view: string;
}
interface RequestDetailsView {
  key: string;
  label: string;
}
interface RequestDetailsDescriptor {
  // First entry is the default view selected when the dialog opens.
  views: (request: MPARequest) => readonly RequestDetailsView[];
  Renderer: React.ComponentType<RequestBodyProps>;
}
const RAW_REQUEST_VIEW: RequestDetailsView = {
  key: 'raw-request',
  label: 'Raw Request'
};
const RawRequestView = t0 => {
  const $ = _c(3);
  const {
    request
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: "16px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== request) {
    t2 = <Stack className="flex-1 min-h-0 overflow-auto" sx={t1}><RequestPathHeader request={request} /><JsonRequestBody request={request} /></Stack>;
    $[1] = request;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const RAW_REQUEST_ONLY: RequestDetailsDescriptor = {
  views: () => [RAW_REQUEST_VIEW],
  Renderer: () => null
};

// Every operation type gets an explicit entry. Default is JSON-only; richer
// renderers (e.g. the policy diff) opt in here.
const requestDetailsByOperation: Partial<Record<MPAOperationType, RequestDetailsDescriptor>> = {
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_ADD_RESTORE_ACCOUNT]: RAW_REQUEST_ONLY,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_RESTORE_RESOURCE]: RAW_REQUEST_ONLY,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_REMOVE_SNAPSHOT_HOLD]: RAW_REQUEST_ONLY,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_UPDATE_NOTIFICATION_POLICY]: {
    views: request => request.resourceStateBefore && request.resourceStateAfter ? [{
      key: 'diff',
      label: 'Diff'
    }, RAW_REQUEST_VIEW] : [RAW_REQUEST_VIEW],
    Renderer: UpdateNotificationPolicyRequestBody
  },
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_DELETE_NOTIFICATION_POLICY]: RAW_REQUEST_ONLY,
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_DELETE_BACKUP_POLICY]: {
    views: () => [{
      key: 'policy',
      label: 'Policy'
    }, RAW_REQUEST_VIEW],
    Renderer: DeleteBackupPolicyRequestBody
  },
  [MPAOperationType.MPA_OPERATION_TYPE_MPA_OPERATION_UPDATE_BACKUP_POLICY]: {
    views: request => request.resourceStateAfter || request.status === MPARequestStatus.MPA_REQUEST_STATUS_PENDING_APPROVAL || request.status === MPARequestStatus.MPA_REQUEST_STATUS_CREATED ? [{
      key: 'diff',
      label: 'Diff'
    }, RAW_REQUEST_VIEW] : [RAW_REQUEST_VIEW],
    Renderer: UpdateBackupPolicyRequestBody
  }
};
const getRequestDetailsDescriptor = (request: MPARequest) => requestDetailsByOperation[request.operation] ?? RAW_REQUEST_ONLY;

// Per-request view-mode state shared between separately-mounted consumers
// (the dialog header's toggle and the dialog body). Backing it with an
// external store lets each consumer be passed independently to the dialog
// framework — the header via header.actions, the body via content — while
// still seeing the same view selection.
interface RequestViewStore {
  view: string;
  listeners: Set<() => void>;
  refCount: number;
}
const requestViewStores = new Map<string, RequestViewStore>();
const getOrCreateRequestViewStore = (request: MPARequest): RequestViewStore => {
  const id = request.id ?? '';
  const existing = requestViewStores.get(id);
  if (existing) {
    return existing;
  }
  const initialView = getRequestDetailsDescriptor(request).views(request)[0].key;
  const store: RequestViewStore = {
    view: initialView,
    listeners: new Set(),
    refCount: 0
  };
  requestViewStores.set(id, store);
  return store;
};
export const useMpaRequestDetailsView = request => {
  const $ = _c(13);
  const id = request.id ?? "";
  let t0;
  if ($[0] !== request) {
    t0 = fn => {
      const store = getOrCreateRequestViewStore(request);
      store.listeners.add(fn);
      return () => {
        store.listeners.delete(fn);
      };
    };
    $[0] = request;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const subscribe = t0;
  let t1;
  if ($[2] !== request) {
    t1 = () => getOrCreateRequestViewStore(request).view;
    $[2] = request;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const getSnapshot = t1;
  const view = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  let t2;
  let t3;
  if ($[4] !== id || $[5] !== request) {
    t2 = () => {
      const store_0 = getOrCreateRequestViewStore(request);
      store_0.refCount = store_0.refCount + 1;
      return () => {
        store_0.refCount = store_0.refCount - 1;
        if (store_0.refCount === 0) {
          requestViewStores.delete(id);
        }
      };
    };
    t3 = [id, request];
    $[4] = id;
    $[5] = request;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  if ($[8] !== request) {
    t4 = next => {
      const store_1 = getOrCreateRequestViewStore(request);
      store_1.view = next;
      store_1.listeners.forEach(_temp);
    };
    $[8] = request;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const setView = t4;
  let t5;
  if ($[10] !== setView || $[11] !== view) {
    t5 = {
      view,
      setView
    };
    $[10] = setView;
    $[11] = view;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};

// Stand-alone toggle that any caller can drop into a dialog header's
// `actions` slot. Stays in sync with the body via the per-request store.
export const MpaRequestViewToggle = t0 => {
  const $ = _c(7);
  const {
    request
  } = t0;
  const views = getRequestDetailsDescriptor(request).views(request);
  const {
    view,
    setView
  } = useMpaRequestDetailsView(request);
  if (views.length <= 1) {
    return null;
  }
  const T0 = ToggleButtonGroup;
  const t1 = "small";
  const t2 = true;
  let t3;
  if ($[0] !== setView) {
    t3 = (_, next) => {
      if (next) {
        setView(next);
      }
    };
    $[0] = setView;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = "mpa-request-view-mode";
  const t5 = views.map(_temp2);
  let t6;
  if ($[2] !== T0 || $[3] !== t3 || $[4] !== t5 || $[5] !== view) {
    t6 = <T0 size={t1} exclusive={t2} value={view} onChange={t3} data-testid={t4}>{t5}</T0>;
    $[2] = T0;
    $[3] = t3;
    $[4] = t5;
    $[5] = view;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
};
export const MpaRequestDetails = t0 => {
  const $ = _c(9);
  const {
    request,
    view
  } = t0;
  let descriptor;
  let t1;
  if ($[0] !== request) {
    descriptor = getRequestDetailsDescriptor(request);
    t1 = descriptor.views(request);
    $[0] = request;
    $[1] = descriptor;
    $[2] = t1;
  } else {
    descriptor = $[1];
    t1 = $[2];
  }
  const views = t1;
  const effectiveView = view ?? views[0].key;
  if (effectiveView === RAW_REQUEST_VIEW.key) {
    let t2;
    if ($[3] !== request) {
      t2 = <RawRequestView request={request} />;
      $[3] = request;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  const {
    Renderer
  } = descriptor;
  let t2;
  if ($[5] !== Renderer || $[6] !== effectiveView || $[7] !== request) {
    t2 = <Stack className="h-full"><Renderer request={request} view={effectiveView} /></Stack>;
    $[5] = Renderer;
    $[6] = effectiveView;
    $[7] = request;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
export const MpaRequestDetailsDialog = t0 => {
  const $ = _c(3);
  const {
    isOpen,
    onClose,
    request
  } = t0;
  if (!isOpen) {
    return null;
  }
  let t1;
  if ($[0] !== onClose || $[1] !== request) {
    t1 = <MpaRequestDetailsDialogContent onClose={onClose} request={request} />;
    $[0] = onClose;
    $[1] = request;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
const MpaRequestDetailsDialogContent = t0 => {
  const $ = _c(20);
  const {
    onClose,
    request
  } = t0;
  const body = parseBody(request);
  const {
    view
  } = useMpaRequestDetailsView(request);
  const T0 = MainDialog;
  const t1 = true;
  const t2 = false;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      height: "90vh"
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== request) {
    t4 = <MpaRequestViewToggle request={request} />;
    $[1] = request;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== onClose || $[4] !== t4) {
    t5 = {
      title: "Request details",
      onClose,
      actions: t4
    };
    $[3] = onClose;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== onClose) {
    t6 = {
      text: "Close",
      onClick: onClose,
      "data-testid": "close-mpa-request-json-viewer"
    };
    $[6] = onClose;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = body ? <CopyToClipboardButton textToCopy={JSON.stringify(body)} buttonLabel="Copy" /> : undefined;
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = {
      primaryButton: t6,
      children: t7
    };
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== request || $[12] !== view) {
    t9 = <Stack className="h-full px-[40px]"><MpaRequestDetails request={request} view={view} /></Stack>;
    $[11] = request;
    $[12] = view;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== T0 || $[15] !== t3 || $[16] !== t5 || $[17] !== t8 || $[18] !== t9) {
    t10 = <T0 isOpen={t1} isLoading={t2} sx={t3} header={t5} footer={t8}>{t9}</T0>;
    $[14] = T0;
    $[15] = t3;
    $[16] = t5;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
};
function _temp(fn_0) {
  return fn_0();
}
function _temp2(v) {
  return <ToggleButton key={v.key} value={v.key}>{v.label}</ToggleButton>;
}